import { deriveEcosystem } from './ecosystemState';

// Derive the command view's state from the command feed
// (GET /apps/:orgId/:appId/command on nventr.io, GET /dev/ops/ecosystem on a
// kit instance — both are the ecosystem feed plus extra blocks). Everything
// the ecosystem scene already animates comes from deriveEcosystem; this adds
// the instrument channels: cost (arc fill / comet speed), uptime ticks,
// usage skyline bars, deploy beads, finding chips, memory gauge.

const fmtUsd = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `$${Number(v).toFixed(2)}`);
const SEV = ['critical', 'high', 'medium', 'low', 'info'];

export function deriveCommand(data) {
  const eco = deriveEcosystem(data);
  const d = data || {};
  const c = d.costs?.current || d.costs || null; // nventr.io: { current, prior }; kit: flat
  const hasCost = !!c && Number.isFinite(Number(c.mtdUsd));
  const limitUsd = Number(d.limits?.app) || null;
  const mtd = hasCost ? Number(c.mtdUsd) || 0 : 0;
  const projected = hasCost ? Number(c.projectedUsd) || 0 : 0;
  const burn = hasCost ? Number(c.burnPerDayUsd) || 0 : 0;
  // Arc fill: against the limit when one exists, else against the projection
  // (so the arc always means "how far through what this month will cost").
  const denom = limitUsd || projected || mtd || 1;
  const cost = {
    available: hasCost,
    mtdUsd: hasCost ? mtd : null,
    burnPerDayUsd: hasCost ? burn : null,
    projectedUsd: hasCost ? projected : null,
    priorUsd: d.costs?.prior?.mtdUsd ?? null,
    priorPeriod: d.costs?.priorPeriod || null,
    limitUsd,
    ratio: hasCost ? mtd / denom : 0,
    ghost: hasCost ? projected / denom : null,
    ticks: limitUsd ? [0.5, 0.8, 1] : [],
    level: !hasCost ? 'off' : limitUsd && mtd >= limitUsd ? 'down' : limitUsd && (mtd >= limitUsd * 0.8 || projected > limitUsd) ? 'warn' : 'ok',
    // Comet: one lap per (24h / burn-normalised) — map $0..$20/day → 0..0.35 laps/s.
    cometSpeed: hasCost && burn > 0 ? Math.min(0.35, 0.03 + burn / 60) : 0,
    infra: c?.infra || null,
    model: c?.model || null,
    dev: c?.dev || null,
    byUser: c?.byUser || {},
    pulledAt: c?.infra?.pulledAt || c?.pulledAt || null,
    text: hasCost ? `${fmtUsd(mtd)} mtd · ${fmtUsd(burn)}/day · ${fmtUsd(projected)} projected${limitUsd ? ` · limit ${fmtUsd(limitUsd)}` : ''}` : '',
  };

  const h = d.health || {};
  const health = {
    days: h.days || [],
    ticks: (h.days || []).map((x) => x.uptime),
    uptime14d: h.uptime14d ?? null,
    p50: h.p50 ?? null,
    p95: h.p95 ?? null,
    features: h.features || { total: 0, passing: 0, failing: [] },
    // Wobble amplitude for the monitor ring: 0 at ≤200ms, 1 at ≥2s p95.
    wobble: h.p95 != null ? Math.min(1, Math.max(0, (h.p95 - 200) / 1800)) : 0,
    incidentsOpen: (d.incidents || d.plane?.incidents || []).filter((i) => i.status === 'open').length,
  };

  const u = d.usage || null;
  const counters = u?.counters || {};
  const USAGE_KEYS = [
    ['tokens', 'tokens'], ['aiTurns', 'turns'], ['pipelineRuns', 'runs'], ['jobRuns', 'jobs'],
    ['agentConversations', 'convos'], ['devCents', 'dev ¢'], ['voiceMinutes', 'voice'],
  ];
  const topUsers = u?.byUser || [];
  const totalTok = Number(counters.tokens) || 0;
  const usage = {
    available: !!u,
    counters,
    bars: USAGE_KEYS.map(([k, label]) => ({
      key: k, label, value: Number(counters[k]) || 0,
      segments: k === 'tokens' && totalTok ? topUsers.slice(0, 3).map((x) => x.tokens / totalTok) : [],
    })).filter((b) => b.value > 0 || ['tokens', 'aiTurns', 'pipelineRuns'].includes(b.key)),
    topUsers,
    reportedAt: u?.reportedAt || null,
    lastActivityAt: u?.lastActivityAt || null,
    statement: d.statement || null,
  };

  const deploys = (d.recentDeploys || d.deploys || []).map((x) => ({
    env: x.env === 'uat' ? 'uat' : 'prod', status: x.status, at: x.at, sha: x.headSha || x.mergeSha || null,
    level: x.status === 'error' ? 'down' : x.status === 'done' ? 'ok' : 'warn',
  }));
  const beads = {
    prod: deploys.filter((x) => x.env === 'prod').slice(0, 5).reverse(),
    uat: deploys.filter((x) => x.env === 'uat').slice(0, 5).reverse(),
  };

  const counts = eco.security.counts || {};
  const security = {
    ...eco.security,
    bySeverity: SEV.map((k) => ({ key: k, count: Number(counts[k]) || 0 })),
    nextBaselineAt: d.scanSchedule?.nextBaselineAt || null,
    assurance: !!(d.instance || d.plane?.instance)?.assurance,
  };

  const host = d.host || null;
  const memory = {
    available: !!host?.load,
    memMb: host?.load?.memMb ?? null,
    cpuPct: host?.load?.cpuPct ?? null,
    share: host?.memShare ?? null,
    hostId: host?.id || null,
    hostVitals: host?.vitals || null,
  };

  const lic = d.license || null;
  const inst = d.instance || d.plane?.instance || {}; // kit feed nests it under plane
  const identity = {
    name: inst.name || eco.core.name,
    status: inst.status || eco.core.status,
    plan: d.plan || inst.plan || null,
    url: inst.url || d.plane?.instance?.url || null,
    kitRef: inst.kitRef || null,
    version: inst.version || null,
    pinnedKitRef: d.pinnedKitRef || null,
    kitDrift: !!(inst.kitRef && d.pinnedKitRef && inst.kitRef !== d.pinnedKitRef),
    prepaidUntil: d.prepaidUntil || null,
    license: lic ? { status: lic.status || lic.state, lastSeenAt: lic.lastSeenAt || null, termEnd: lic.termEnd || lic.graceUntil || null } : (lic === null && d.license !== undefined ? null : eco.plane.license ? { status: eco.plane.license } : null),
    period: d.period || null,
    assurance: !!inst.assurance,
  };

  return { ...eco, cost, health, usage, beads, security, memory, identity };
}
